"use client";

import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck, CheckCircle, AlertCircle } from "lucide-react";
import { authService } from "../../apis/authService";

export function ResetPasswordForm() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // Password strength criteria
  const criteria = [
    { label: "At least 8 characters", test: (p) => p.length >= 8 },
    { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
    { label: "One number", test: (p) => /[0-9]/.test(p) },
    { label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
  ];

  const passedCount = criteria.filter((c) => c.test(newPassword)).length;

  const strengthConfig = [
    { label: "", color: "bg-gray-200" },
    { label: "Weak", color: "bg-red-400" },
    { label: "Fair", color: "bg-orange-400" },
    { label: "Good", color: "bg-yellow-400" },
    { label: "Strong", color: "bg-green-500" },
  ];
  const { label: strengthLabel, color: strengthColor } = strengthConfig[passedCount];

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!newPassword || !confirmPassword) {
      setMessage({ type: "error", text: "Both fields are required" });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "Passwords do not match" });
      return;
    }
    if (passedCount !== 4) {
      setMessage({ type: "error", text: "Please meet all password requirements" });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      const result = await authService.resetPassword(token, newPassword, confirmPassword);
      if (result.success) {
        setMessage({ type: "success", text: result.message || "Password reset successful! Redirecting..." });
        setTimeout(() => navigate("/signin"), 3000);
      } else {
        setMessage({ type: "error", text: result.message || "Password reset failed" });
      }
    } catch {
      setMessage({ type: "error", text: "Password reset failed. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Success / Error message */}
      {message && (
        <div
          className={`p-3 rounded-lg flex items-center gap-2 ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border border-green-200"
              : "bg-red-50 text-red-800 border border-red-200"
          }`}
        >
          {message.type === "success" ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-red-600" />
          )}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      {/* Icon */}
      <div className="flex justify-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-teal-600 flex items-center justify-center shadow-lg">
          <ShieldCheck className="w-8 h-8 text-white" />
        </div>
      </div>

      <div className="text-center">
        <h2 className="text-2xl font-semibold text-gray-900">Create new password</h2>
        <p className="text-sm text-gray-500 mt-1">Must be at least 8 characters</p>
      </div>

      <form onSubmit={handleSubmit} className="text-left">
        {/* New Password field */}
        <div className="relative mb-2">
          <input
            type={showNew ? "text" : "password"}
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full h-12 px-4 pr-10 border border-gray-200 rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder-gray-400"
            required
          />
          <button
            type="button"
            onClick={() => setShowNew(!showNew)}
            className="absolute right-3 top-3.5 text-gray-400"
          >
            {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {/* Strength meter (only when password is not empty) */}
        {newPassword && (
          <div className="mb-3">
            <div className="flex gap-1.5 items-center">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${
                    i <= passedCount ? strengthColor : "bg-gray-200"
                  }`}
                />
              ))}
              <span
                className={`text-xs font-semibold ml-1 whitespace-nowrap ${
                  passedCount === 4
                    ? "text-green-600"
                    : passedCount >= 3
                    ? "text-yellow-500"
                    : passedCount >= 2
                    ? "text-orange-400"
                    : "text-red-400"
                }`}
              >
                {strengthLabel}
              </span>
            </div>
          </div>
        )}

        {/* Criteria checklist */}
        {newPassword && (
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mb-4">
            {criteria.map((c) => {
              const ok = c.test(newPassword);
              return (
                <div key={c.label} className="flex items-center gap-1.5">
                  <div
                    className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                      ok ? "bg-green-500" : "bg-gray-200"
                    }`}
                  >
                    {ok ? (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10">
                        <path
                          d="M2 5l2.5 2.5L8 3"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg className="w-2.5 h-2.5 text-gray-400" fill="none" viewBox="0 0 10 10">
                        <path
                          d="M3 3l4 4M7 3L3 7"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </div>
                  <span className={`text-xs leading-tight ${ok ? "text-gray-700" : "text-gray-400"}`}>
                    {c.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Confirm Password field */}
        <div className="relative mb-6">
          <input
            type={showConfirm ? "text" : "password"}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className={`w-full h-12 px-4 pr-10 border rounded-xl bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm placeholder-gray-400 transition ${
              confirmPassword && confirmPassword !== newPassword
                ? "border-red-300"
                : "border-gray-200"
            }`}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-3.5 text-gray-400"
          >
            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
          {confirmPassword && confirmPassword !== newPassword && (
            <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
          )}
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-12 bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white rounded-full font-semibold transition text-sm disabled:opacity-70"
        >
          {isLoading ? "Resetting..." : "Reset Password"}
        </button>
      </form>

      {/* Back to Sign In link */}
      <div className="text-center">
        <button
          onClick={() => navigate("/signin")}
          className="text-sm text-blue-600 hover:text-blue-800 transition"
        >
          ← Back to Sign In
        </button>
      </div>
    </div>
  );
}